import type { MouseEvent } from 'react';
import { Box, Typography, Stack, Container } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Button } from '@classity-edtech/ui-base/dist/components/Inputs/Button/Button';
import useAppMediaQuery from '@classity-edtech/ui-base/dist/hooks/useMediaQuery';
import { globalColours } from '@classity-edtech/ui-base/dist/style/source';
import { MapMarkerIcon } from '@classity-edtech/ui-base/dist/components/Displays';

export interface MastheadCtaButton {
  label: string;
  variant?: 'outlined';
  href?: string;
}

export interface MastheadProps {
  className?: string;
  imgMasthead: string;
  heading: string;
  paragraph: string;
  ctaButtons: MastheadCtaButton[];
  address: string;
}

export const Masthead = ({
  className,
  imgMasthead,
  heading,
  paragraph,
  ctaButtons,
  address,
}: MastheadProps) => {
  const screens = useAppMediaQuery();
  const isMobile = screens.isMobile || screens.isTablet;

  const scrollToSection = (href?: string) => {
    if (!href || typeof document === 'undefined') {
      return;
    }

    const targetId = href.startsWith('#') ? href.slice(1) : href.split('#')[1];

    if (!targetId) {
      return;
    }

    const section = document.getElementById(targetId);

    if (section) {
      section.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleJumpClick = (
    event: MouseEvent<HTMLButtonElement | HTMLAnchorElement>,
    href?: string,
  ) => {
    if (!href || (!href.startsWith('#') && !href.includes('#'))) {
      return;
    }

    event.preventDefault();
    scrollToSection(href);
  };

  return (
    <Box
      className={className}
      sx={{
        position: 'relative',
        width: '100%',
        height: { xs: 'auto', md: '1012px' },
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        mt: { xs: '-82px', md: '-82px' },
      }}
    >
      {/* Background Image with Overlay */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
        }}
      >
        <Box
          component='img'
          alt=''
          src={imgMasthead}
          sx={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            maxWidth: 'none',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(to right, rgba(0,0,0,0.95), rgba(0,0,0,0.7))',
          }}
        />
      </Box>

      {/* Content */}
      <Container
        sx={{
          position: 'relative',
          py: { xs: '152.5px', md: '278px' },
        }}
      >
        <Stack
          sx={{
            gap: 0,
            width: '100%',
            maxWidth: { xs: '100%', md: '768px' },
          }}
        >
          {/* Heading */}
          <Box
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}
          >
            <Typography
              variant={isMobile ? 'subtitle2' : 'subtitle1'}
              variantMapping={{ subtitle2: 'h1', subtitle1: 'h1' }}
              sx={{
                color: globalColours['$global-colour-base-contrast'],
                width: '100%',
                wordBreak: 'break-word',
                lineHeight: { xs: 'normal', md: '100px' },
                letterSpacing: { xs: 0, md: '-2.5px' },
                fontFamily: isMobile ? 'Gill Sans, sans-serif' : 'Gill Sans Infant Std, serif',
                fontSize: { xs: '48px', md: '96px' },
                fontWeight: isMobile ? 400 : 500,
              }}
            >
              {heading}
            </Typography>
          </Box>

          {/* Paragraph */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pb: { xs: 4, md: 4 },
              width: '100%',
            }}
          >
            <Typography
              variant='body2'
              sx={{
                color: globalColours['$global-colour-base-contrast'],
                width: '100%',
                wordBreak: 'break-word',
              }}
            >
              {paragraph}
            </Typography>
          </Box>

          {/* CTA Buttons */}
          <Stack
            sx={{
              gap: { xs: 1.5, md: 0 },
              pb: { xs: 2, md: 4 },
              width: '100%',
            }}
          >
            {/* Primary CTAs Row (Mobile: side by side) */}
            <Stack
              direction='row'
              spacing={{ xs: 1.5, md: 3 }}
              sx={{
                width: '100%',
                display: ctaButtons.length > 2 ? { xs: 'flex', md: 'none' } : 'flex',
              }}
            >
              {ctaButtons.slice(0, isMobile ? 2 : ctaButtons.length).map((button, index) => (
                <Button
                  key={`${button.label}-${index}`}
                  variant={button.variant === 'outlined' ? 'outlined' : undefined}
                  href={button.href}
                  onClick={event => handleJumpClick(event, button.href)}
                  sx={{
                    backgroundColor:
                      button.variant === 'outlined'
                        ? 'transparent'
                        : globalColours['$global-colour-base-contrast'],
                    color:
                      button.variant === 'outlined'
                        ? globalColours['$global-colour-base-contrast']
                        : globalColours['$global-colour-base-dark'],
                    border:
                      button.variant === 'outlined'
                        ? `2px solid ${globalColours['$global-colour-base-contrast']}`
                        : undefined,
                    px: { xs: '29px', md: button.variant === 'outlined' ? '33px' : '29px' },
                    py: 2,
                    flex: isMobile && index < 2 ? '1 0 0' : undefined,
                    minWidth: isMobile && index < 2 ? 0 : undefined,
                    height: { xs: '58px', md: 'auto' },
                    borderRadius: '12px',
                    boxShadow:
                      button.variant === 'outlined'
                        ? undefined
                        : '0px 10px 7.5px rgba(0,0,0,0.1), 0px 4px 3px rgba(0,0,0,0.1)',
                    '&:hover': {
                      backgroundColor:
                        button.variant === 'outlined'
                          ? 'rgba(255,255,255,0.08)'
                          : globalColours['$global-colour-background-neutral-hover'],
                      border:
                        button.variant === 'outlined'
                          ? `2px solid ${globalColours['$global-colour-base-contrast']}`
                          : undefined,
                    },
                  }}
                >
                  <Typography
                    variant='body2SemiBold'
                    sx={{
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {button.label}
                  </Typography>
                </Button>
              ))}
            </Stack>

            {/* Desktop: All buttons in row */}
            {!isMobile && ctaButtons.length > 2 && (
              <Stack direction='row' spacing={3} sx={{ width: '100%' }}>
                {ctaButtons.map((button, index) => (
                  <Button
                    key={`${button.label}-${index}`}
                    variant={button.variant === 'outlined' ? 'outlined' : undefined}
                    href={button.href}
                    onClick={event => handleJumpClick(event, button.href)}
                    sx={{
                      backgroundColor:
                        button.variant === 'outlined'
                          ? 'transparent'
                          : globalColours['$global-colour-base-contrast'],
                      color:
                        button.variant === 'outlined'
                          ? globalColours['$global-colour-base-contrast']
                          : globalColours['$global-colour-base-dark'],
                      border:
                        button.variant === 'outlined'
                          ? `2px solid ${globalColours['$global-colour-base-contrast']}`
                          : undefined,
                      px: button.variant === 'outlined' ? '33px' : '29px',
                      py: 2,
                      borderRadius: '12px',
                      boxShadow:
                        button.variant === 'outlined'
                          ? undefined
                          : '0px 10px 7.5px rgba(0,0,0,0.1), 0px 4px 3px rgba(0,0,0,0.1)',
                      '&:hover': {
                        backgroundColor:
                          button.variant === 'outlined'
                            ? 'rgba(255,255,255,0.08)'
                            : globalColours['$global-colour-background-neutral-hover'],
                        border:
                          button.variant === 'outlined'
                            ? `2px solid ${globalColours['$global-colour-base-contrast']}`
                            : undefined,
                        color: globalColours['$global-colour-base-contrast'],
                      },
                    }}
                  >
                    <Typography
                      variant='body2SemiBold'
                      sx={{
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {button.label}
                    </Typography>
                  </Button>
                ))}
              </Stack>
            )}

            {/* Mobile: Third button full width */}
            {isMobile && ctaButtons.length > 2 && (
              <Button
                variant={ctaButtons[2].variant === 'outlined' ? 'outlined' : undefined}
                href={ctaButtons[2].href}
                onClick={event => handleJumpClick(event, ctaButtons[2].href)}
                sx={{
                  backgroundColor:
                    ctaButtons[2].variant === 'outlined'
                      ? 'transparent'
                      : globalColours['$global-colour-base-contrast'],
                  color:
                    ctaButtons[2].variant === 'outlined'
                      ? globalColours['$global-colour-base-contrast']
                      : globalColours['$global-colour-base-dark'],
                  border:
                    ctaButtons[2].variant === 'outlined'
                      ? `2px solid ${globalColours['$global-colour-base-contrast']}`
                      : undefined,
                  px: '33px',
                  py: '18px',
                  height: '60px',
                  borderRadius: '12px',
                  width: '100%',
                  '&:hover': {
                    backgroundColor:
                      ctaButtons[2].variant === 'outlined'
                        ? 'rgba(255,255,255,0.08)'
                        : globalColours['$global-colour-background-neutral-hover'],
                    border:
                      ctaButtons[2].variant === 'outlined'
                        ? `2px solid ${globalColours['$global-colour-base-contrast']}`
                        : undefined,
                  },
                }}
              >
                <Typography
                  variant='body2SemiBold'
                  sx={{
                    whiteSpace: 'nowrap',
                  }}
                >
                  {ctaButtons[2].label}
                </Typography>
              </Button>
            )}
          </Stack>

          {/* Address */}
          <Box sx={{ display: 'flex', alignItems: 'left', justifyContent: 'left', width: '100%' }}>
            <Stack
              direction='row'
              spacing={1.5}
              sx={{
                alignItems: isMobile ? 'flex-start' : 'flex-end',
                pr: 1,
                py: 1,
                flex: isMobile ? '1 0 0' : undefined,
                width: isMobile ? '100%' : 'auto',
              }}
            >
              <Box
                sx={{
                  width: { xs: '24px', md: '32px' },
                  height: { xs: '24px', md: '32px' },
                  position: 'relative',
                  overflow: 'hidden',
                  flexShrink: 0,
                }}
              >
                <MapMarkerIcon
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    width: '100%',
                    height: '100%',
                  }}
                />
              </Box>
              <Typography
                variant='body2'
                sx={{
                  color: globalColours['$global-colour-base-contrast'],
                  flex: isMobile ? '1 0 0' : undefined,
                  minWidth: isMobile ? 0 : undefined,
                }}
              >
                {address}
              </Typography>
            </Stack>
          </Box>
        </Stack>
      </Container>
    </Box>
  );
};

export default styled<any>(Masthead)(({ theme }) => ``);
